import { useState } from 'react'
import {
  KpiStrip, MetricCard, Panel, SectionHeader,
} from '@/components/erp/primitives'
import { RankedBars } from '@/shared/ui/charts'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { spendAnalysis, type SpendAnalysis, type SpendSlice } from './api'

/**
 * Where the money went.
 *
 * ## The sentence this page exists to say
 *
 * *You have spent 4.02m since July. Posters and rallies are two thirds of it. 840,000 of it
 * has no receipt behind it.* Everything else on the screen supports one of those three
 * clauses, and nothing is here that would need explaining first.
 *
 * ## Why the receipt gap is money and not a count
 *
 * "Forty entries have no receipt" does not tell a campaign manager whether to worry.
 * "840,000 shillings has no receipt behind it" does: it is the figure that gets questioned,
 * and it is what a return is challenged on.
 *
 * ## Gross, corrected, net — all three
 *
 * A single net figure is one the reader has to trust. "Spent 4.2m, corrected 180k, so 4.02m
 * stands" is one they can check, and it makes the reversals visible rather than silently
 * folded into a smaller number.
 */
export function FinanceAnalysisPage() {
  const canAnalyse = useCan('finance:analyse')
  const [days, setDays] = useState(90)

  const to = new Date()
  const from = new Date(to.getTime() - days * 86_400_000)
  const iso = (d: Date) => d.toISOString().slice(0, 10)

  const analysis = useLoad(
    () => spendAnalysis({ from: iso(from), to: iso(to) }), [days])

  if (!canAnalyse) return <Forbidden what="Campaign spending analysis" />

  const d = analysis.data

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Where the money went"
        description="What the campaign has spent on itself — rallies, transport, printing,
                     airtime, allowances. Programme spending on beneficiaries is under
                     Programmes, and is not counted here."
        right={(
          <div className="flex items-center gap-1">
            {[30, 90, 180, 365].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setDays(n)}
                className={
                  'rounded border px-2 py-1 text-erp-xs '
                  + (n === days
                    ? 'border-foreground bg-muted font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted/50')
                }
              >
                {n === 365 ? '1 year' : `${n} days`}
              </button>
            ))}
          </div>
        )}
      />

      <ErrorStrip message={analysis.error} />

      {analysis.loading && !d ? <LoadingBlock label="Adding it up…" /> : !d ? null : (
        <Body d={d} />
      )}
    </div>
  )
}

function Body({ d }: { d: SpendAnalysis }) {
  const money = (n: number | null | undefined) =>
    n == null ? '—' : `${d.currency ?? ''} ${fmtNumber(Math.round(n))}`.trim()

  if (d.entries === 0 && d.reversals === 0) {
    return (
      <Panel title="Nothing recorded in this window">
        <p className="p-3 text-erp-sm text-muted-foreground">
          No campaign spending has been recorded between {d.from} and {d.to}. Record the
          first entry on <b>The ledger</b> tab — a fuel receipt, a printing invoice, a hall
          hire. The analysis fills in from there.
        </p>
      </Panel>
    )
  }

  return (
    <div className="space-y-4">
      {/* The headline, in words, before any chart. */}
      <Panel>
        <div className="p-3">
          {d.mixedCurrencyReason ? (
            <p className="text-erp-base">{d.mixedCurrencyReason}</p>
          ) : (
            <p className="text-erp-base">
              You have spent <b>{money(d.net)}</b> between {d.from} and {d.to}
              {d.reversals > 0 ? (
                <> — <b>{money(d.gross)}</b> recorded, with <b>{money(d.reversed)}</b> since
                  corrected</>
              ) : null}.
              {d.byCategory[0]?.share != null && d.byCategory[0].share > 0.2 ? (
                <> The largest heading is <b>{d.byCategory[0].label.toLowerCase()}</b>, at{' '}
                  {Math.round(d.byCategory[0].share * 100)}% of it.</>
              ) : null}
            </p>
          )}
          {d.withoutReceipt > 0 ? (
            <p className="mt-1.5 text-erp-sm text-destructive">
              <b>{money(d.withoutReceiptAmount)}</b> of that has no receipt behind it
              ({d.withoutReceipt} {d.withoutReceipt === 1 ? 'entry' : 'entries'}). That is the
              figure a return gets challenged on.
            </p>
          ) : (
            <p className="mt-1.5 text-erp-sm text-muted-foreground">
              Every entry has a receipt attached.
            </p>
          )}
          {d.disputedReceipts > 0 ? (
            <p className="mt-1 text-erp-sm text-amber-600 dark:text-amber-500">
              {d.disputedReceipts}{' '}
              {d.disputedReceipts === 1 ? 'receipt reads' : 'receipts read'} a different
              amount from what was typed. Worth opening before anybody else does.
            </p>
          ) : null}
        </div>
      </Panel>

      <KpiStrip>
        <MetricCard label="Spent, net of corrections" value={money(d.net)}
          sub={`${fmtNumber(d.entries)} entries`} />
        <MetricCard label="Corrected" value={money(d.reversed)}
          sub={`${d.reversals} reversing ${d.reversals === 1 ? 'entry' : 'entries'}`}
          accent={d.reversals > 0} />
        <MetricCard label="A day, on average" value={money(d.perDay)}
          sub="across this window" />
        <MetricCard label="No receipt" value={money(d.withoutReceiptAmount)}
          sub={`${d.withoutReceipt} of ${fmtNumber(d.entries)} entries`}
          accent={d.withoutReceipt > 0} />
      </KpiStrip>

      <div className="grid gap-3 lg:grid-cols-2">
        <Slices title="What it went on" slices={d.byCategory} currency={d.currency}
          empty="Nothing recorded." />
        <Slices title="How it was paid" slices={d.byMethod} currency={d.currency}
          empty="Nothing recorded."
          note="In-kind means goods or services given rather than bought. They are declarable
                at their value, which is why they are counted here." />
        <Slices title="Where it was spent" slices={d.byUnit} currency={d.currency}
          empty="Nothing is tied to a place yet."
          note="Office rent and professional fees are not spent anywhere in particular and
                show as 'not tied to a place'. That is correct, not missing data." />
        <Slices title="Which rallies and meetings" slices={d.byEvent} currency={d.currency}
          empty="Nothing is tied to an event yet." />
      </div>
    </div>
  )
}

function Slices({ title, slices, currency, empty, note }: {
  title: string
  slices: SpendSlice[]
  currency: string | null
  empty: string
  note?: string
}) {
  const points = slices
    .filter((s) => s.amount != null)
    .slice(0, 12)
    .map((s) => ({
      label: s.label,
      value: Math.round(s.amount as number),
      hint: `${s.entries} ${s.entries === 1 ? 'entry' : 'entries'}`
        + (s.share != null ? ` · ${Math.round(s.share * 100)}% of the total` : ''),
    }))

  return (
    <Panel title={title} description={note}>
      <RankedBars
        points={points}
        emptyMessage={empty}
        formatValue={(n) => `${currency ?? ''} ${fmtNumber(n)}`.trim()}
      />
    </Panel>
  )
}
